import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { GraftonChat } from "@/components/GraftonChat";
import {
  CreditCard, ExternalLink, Plus, Package, ArrowRight, CheckCircle2,
  Wallet, TrendingUp, ShoppingBag, Sparkles, RefreshCw, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

const PaymentDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stripeConnected, setStripeConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [products, setProducts] = useState<Array<{ id: string; name: string; price: number | null; category: string }>>([]);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user) {
      supabase
        .from("agent_products")
        .select("id, name, price, category")
        .eq("user_id", user.id)
        .eq("enabled", true)
        .then(({ data }) => {
          if (data) setProducts(data);
        });
    }
  }, [user]);

  const handleConnectStripe = () => {
    // In production this would redirect to Stripe Connect onboarding
    toast.success("Redirection vers Stripe Connect...");
    setStripeConnected(true);
  };

  const handleSyncProducts = async () => {
    if (products.length === 0) {
      toast.error("Aucun produit à synchroniser. Ajoutez des produits dans NanoCorp d'abord.");
      return;
    }
    setSyncing(true);
    // Simulate sync - in production would call Stripe API
    await new Promise((r) => setTimeout(r, 2000));
    toast.success(`${products.length} produit(s) synchronisé(s) avec Stripe !`);
    setSyncing(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background flex w-full">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
            <div className="flex h-14 items-center gap-3 px-4">
              <SidebarTrigger className="text-muted-foreground" />
              <CreditCard className="h-5 w-5 text-primary" />
              <h1 className="font-semibold text-lg">Paiements</h1>
            </div>
          </nav>

          <main className="flex-1 px-4 md:px-8 py-8 space-y-6 max-w-5xl">
            {/* Stripe Connect */}
            <Card className="border-primary/20 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-500/10 via-primary/5 to-transparent p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                        <CreditCard className="h-5 w-5 text-violet-400" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">Stripe</h2>
                        <p className="text-xs text-muted-foreground">Paiements sécurisés en ligne</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Acceptez les paiements par carte, gérez vos abonnements et suivez vos revenus en temps réel.
                    </p>
                  </div>
                  {stripeConnected ? (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-sm font-medium">Connecté</span>
                    </div>
                  ) : (
                    <Button onClick={handleConnectStripe} className="gap-2 bg-violet-600 hover:bg-violet-700">
                      <ExternalLink className="h-4 w-4" />
                      Créer / Connecter mon compte Stripe
                    </Button>
                  )}
                </div>
              </div>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Wallet, label: "Solde disponible", value: stripeConnected ? "1 240,00 €" : "—", color: "text-emerald-400", bg: "bg-emerald-500/10" },
                { icon: TrendingUp, label: "Revenus du mois", value: stripeConnected ? "8 450,00 €" : "—", color: "text-primary", bg: "bg-primary/10" },
                { icon: ShoppingBag, label: "Transactions", value: stripeConnected ? "34" : "—", color: "text-violet-400", bg: "bg-violet-500/10" },
              ].map((stat, i) => (
                <Card key={i}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                        <stat.icon className={`h-5 w-5 ${stat.color}`} />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                        <p className="text-xl font-bold">{stat.value}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Product Sync */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Package className="h-5 w-5 text-primary" />
                      Catalogue Produits Stripe
                    </CardTitle>
                    <CardDescription>
                      Synchronisez vos produits Grafton avec votre catalogue Stripe
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncProducts}
                    disabled={syncing || !stripeConnected}
                    className="gap-2"
                  >
                    {syncing ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    {syncing ? "Synchronisation..." : "Synchroniser"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {products.length > 0 ? (
                  <div className="space-y-2">
                    {products.map((product) => (
                      <div
                        key={product.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Package className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{product.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{product.category}</p>
                          </div>
                        </div>
                        <span className="text-sm font-semibold">
                          {product.price ? `${product.price.toLocaleString("fr-FR")} €` : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center rounded-lg bg-secondary/20 border border-dashed border-border/50">
                    <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Aucun produit trouvé. Ajoutez vos produits dans{" "}
                      <button
                        onClick={() => navigate("/nanocorp")}
                        className="text-primary hover:underline font-medium"
                      >
                        NanoCorp → Produits
                      </button>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Other payment methods */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Autres moyens de paiement</CardTitle>
                <CardDescription>Bientôt disponibles</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { name: "PayPal", soon: true },
                    { name: "Apple Pay", soon: true },
                    { name: "Google Pay", soon: true },
                    { name: "Virement SEPA", soon: true },
                  ].map((method) => (
                    <div
                      key={method.name}
                      className="p-3 rounded-xl bg-secondary/30 border border-border/50 text-center opacity-60"
                    >
                      <p className="text-sm font-medium">{method.name}</p>
                      <span className="text-[10px] text-muted-foreground">Bientôt</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </main>

          <GraftonChat context={{ page: "paiement" }} />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default PaymentDashboard;
